import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChatComponent } from '../../chat/chat.component';
import { TaskQuickCreateModalComponent } from '../../tasks/components/task-quick-create-modal/task-quick-create-modal.component';
import { LinkType, TaskCreateContext } from '../../tasks/models/task.models';
import { CommentsPanelComponent } from '../comments/comments-panel.component';
import { EntityChannelCreatePayload, EntityChannelPickerComponent } from '../entity-channel-picker/entity-channel-picker.component';
import { Channel } from '../models/chat.models';
import { ChatService } from '../services/chat.service';
import { FabContext, FabService } from '../services/fab.service';

@Component({
  selector: 'app-global-fab',
  templateUrl: './global-fab.component.html',
  styleUrls: ['./global-fab.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DialogModule,
    ChatComponent,
    CommentsPanelComponent,
    TaskQuickCreateModalComponent,
    EntityChannelPickerComponent,
  ],
})
export class GlobalFabComponent implements OnInit, OnDestroy {
  isOpen = false;
  totalUnread = 0;
  directUnread = 0;
  channelUnread = 0;

  showChatModal = false;
  showCommentsPanel = false;
  showTaskModal = false;
  showEntityChannelPicker = false;

  pendingChannelId: number | null = null;
  entityChannels: Channel[] = [];
  context: FabContext | null = null;
  taskContext: TaskCreateContext | null = null;

  private destroy$ = new Subject<void>();
  private pollTimer?: ReturnType<typeof setInterval>;

  constructor(
    private chatService: ChatService,
    private fabService: FabService,
    private cdr: ChangeDetectorRef,
    public router: Router
  ) {}

  ngOnInit(): void {
    // this.chatService.totalUnread$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
    //   this.totalUnread = count;
    //   this.cdr.markForCheck();
    // });

    this.chatService.directUnread$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.directUnread = count;
        this.totalUnread = this.directUnread + this.channelUnread;
        this.cdr.markForCheck();
      });

    this.chatService.channelUnread$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.channelUnread = count;
        this.totalUnread = this.directUnread + this.channelUnread;
        this.cdr.markForCheck();
      });

    // this.chatService.openChatModal$.pipe(takeUntil(this.destroy$)).subscribe((channelId) => {
    //   this.pendingChannelId = channelId;
    //   this.showChatModal = true;
    //   this.isOpen = false;
    //   this.cdr.markForCheck();
    // });

    this.fabService.context$.pipe(takeUntil(this.destroy$)).subscribe((ctx) => {
      this.context = ctx;
      // Close comments panel if entity context is removed
      if (!ctx) {
        this.showCommentsPanel = false;
      }
      this.cdr.markForCheck();
    });

    if (this.chatService.hasAuthContext()) {
      this.chatService.connectWebSocket();
      this.loadUnreads();
      this.pollTimer = setInterval(() => this.loadUnreads(), 60_000);
    }
  }

  private loadUnreads(): void {
    if (!this.chatService.hasAuthContext()) {
      return;
    }

    this.chatService
      .getMyChannels()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.chatService.updateUnreadCounts(res.channels),
      });
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    this.cdr.markForCheck();
  }

  openChat(): void {
    this.isOpen = false;

    if (!this.context) {
      this.chatService.openFooterChat();
      this.cdr.markForCheck();
      return;
    }

    const { entityType, entityId, entityDisplayName } = this.context;
    const defaultChannelName =
      entityDisplayName || `${entityType} #${entityId}`;

    this.chatService
      .findEntityChannels(entityType, entityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels) => {
          if (channels.length === 0) {
            this.chatService.openEntityChat(
              entityType,
              entityId,
              defaultChannelName
            );
          } else {
            this.entityChannels = channels;
            this.showEntityChannelPicker = true;
            this.cdr.markForCheck();
          }
        },
      });

    this.cdr.markForCheck();
  }

  onEntityChannelSelected(channelId: number): void {
    this.chatService.openFooterChat(channelId, {
      channelsSubTab: 'entity_linked',
    });
    this.showEntityChannelPicker = false;
    this.entityChannels = [];
    this.cdr.markForCheck();
  }

  onCreateEntityChannel(payload: EntityChannelCreatePayload): void {
    if (!this.context) {
      return;
    }

    const { entityType, entityId } = this.context;

    this.chatService
      .createEntityLinkedChannel(
        entityType,
        entityId,
        payload.channelName,
        payload.memberIds
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channelId) => {
          this.chatService.openFooterChat(channelId, {
            channelsSubTab: 'entity_linked',
          });
          this.showEntityChannelPicker = false;
          this.entityChannels = [];
          this.cdr.markForCheck();
        },
      });
  }

  openComments(): void {
    this.isOpen = false;
    this.showCommentsPanel = true;
    this.cdr.markForCheck();
  }

  popOutChat(): void {
    const base = '/chat';
    const url = this.pendingChannelId ? `${base}?channelId=${this.pendingChannelId}` : base;
    window.open(url, '_blank', 'width=1200,height=760,resizable=yes,scrollbars=no');
    this.showChatModal = false;
    this.cdr.markForCheck();
  }

  openTask(): void {
    this.isOpen = false;
    this.taskContext = this.context
      ? {
          entityType: this.context.entityType as any,
          entityId: this.context.entityId,
          entityDisplayName: this.context.entityDisplayName,
          linkType: LinkType.PRIMARY,
        }
      : null;
    this.showTaskModal = true;
    this.cdr.markForCheck();
  }

  onTaskCreated(_taskId: number | null): void {
    this.showTaskModal = false;
    this.taskContext = null;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    clearInterval(this.pollTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
